import type { SystemSettingsResponse } from '../services/api';
import {
  DEFAULT_PRIMARY_COLOR,
  DEFAULT_SECONDARY_COLOR,
  HEX_COLOR_REGEX,
} from '../types/systemSettings';

export type ThemeMode = 'light' | 'dark';
export type ThemePreference = 'system' | ThemeMode;

const clamp = (value: number, min: number, max: number): number => Math.min(max, Math.max(min, value));

const componentToHex = (value: number): string => {
  const hex = clamp(Math.round(value), 0, 255).toString(16).toUpperCase();
  return hex.length === 1 ? `0${hex}` : hex;
};

const hexToRgb = (hex: string): { r: number; g: number; b: number } | null => {
  if (!hex) return null;
  let normalized = hex.trim().replace('#', '');

  if (normalized.length === 3) {
    normalized = normalized
      .split('')
      .map((char) => `${char}${char}`)
      .join('');
  }

  if (normalized.length !== 6) return null;

  const r = Number.parseInt(normalized.slice(0, 2), 16);
  const g = Number.parseInt(normalized.slice(2, 4), 16);
  const b = Number.parseInt(normalized.slice(4, 6), 16);

  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) {
    return null;
  }

  return { r, g, b };
};

const rgbToHex = (r: number, g: number, b: number): string => `#${componentToHex(r)}${componentToHex(g)}${componentToHex(b)}`;

const mixHexColors = (colorA: string, colorB: string, weightA: number, fallback: string): string => {
  const ratio = Number.isFinite(weightA) ? clamp(weightA, 0, 1) : 0.5;
  const rgbA = hexToRgb(colorA);
  const rgbB = hexToRgb(colorB);

  if (!rgbA || !rgbB) {
    return fallback;
  }

  const r = rgbA.r * ratio + rgbB.r * (1 - ratio);
  const g = rgbA.g * ratio + rgbB.g * (1 - ratio);
  const b = rgbA.b * ratio + rgbB.b * (1 - ratio);

  return rgbToHex(r, g, b);
};

const relativeLuminance = (hex: string): number => {
  const rgb = hexToRgb(hex);
  if (!rgb) {
    return 0;
  }

  const toLinear = (channel: number): number => {
    const srgb = channel / 255;
    return srgb <= 0.03928 ? srgb / 12.92 : ((srgb + 0.055) / 1.055) ** 2.4;
  };

  const r = toLinear(rgb.r);
  const g = toLinear(rgb.g);
  const b = toLinear(rgb.b);

  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

const sanitizeColor = (value: string | null | undefined, fallback: string): string => {
  if (!value || typeof value !== 'string') {
    return fallback;
  }

  const trimmed = value.trim().toUpperCase();
  if (!HEX_COLOR_REGEX.test(trimmed)) {
    return fallback;
  }

  return trimmed;
};

const ensureReadableOnDark = (color: string, fallback: string, minLuminance = 0.6): string => {
  if (!HEX_COLOR_REGEX.test(color)) {
    return fallback;
  }

  const luminance = relativeLuminance(color);
  if (luminance >= minLuminance) {
    return color;
  }

  const deficit = clamp(minLuminance - luminance, 0, 0.75);
  const blendRatio = clamp(0.45 + deficit * 1.35, 0.45, 0.92);

  return mixHexColors('#FFFFFF', color, blendRatio, fallback);
};

const getContrastText = (hex: string, lightFallback = '#0F172A', darkFallback = '#FFFFFF'): string => {
  return relativeLuminance(hex) > 0.53 ? lightFallback : darkFallback;
};

const lighten = (hex: string, amount: number, fallback: string): string => {
  return mixHexColors('#FFFFFF', hex, clamp(amount, 0, 1), fallback);
};

const darken = (hex: string, amount: number, fallback: string): string => {
  return mixHexColors('#000000', hex, clamp(amount, 0, 1), fallback);
};

interface DerivedTheme {
  mode: ThemeMode;
  primary: string;
  primaryContrast: string;
  primaryStrong: string;
  primarySoft: string;
  primarySubtle: string;
  secondary: string;
  secondaryContrast: string;
  secondarySoft: string;
  background: string;
  surface: string;
  surfaceMuted: string;
  surfaceElevated: string;
  border: string;
  textStrong: string;
  textBase: string;
  textMuted: string;
  accent: string;
  accentSoft: string;
  footerBackground: string;
  footerText: string;
}

const deriveLightTheme = (primaryInput: string, secondaryInput: string): DerivedTheme => {
  const primary = sanitizeColor(primaryInput, DEFAULT_PRIMARY_COLOR);
  const secondary = sanitizeColor(secondaryInput, DEFAULT_SECONDARY_COLOR);

  const primaryContrast = getContrastText(primary);
  const secondaryContrast = getContrastText(secondary);

  const primaryStrong = darken(primary, 0.35, primary);
  const primarySoft = lighten(primary, 0.82, primary);
  const primarySubtle = lighten(primary, 0.92, primary);

  const secondarySoft = lighten(secondary, 0.88, secondary);

  const background = lighten(primary, 0.96, '#F8F9FA');
  const surface = lighten(primary, 0.9, '#FFFFFF');
  const surfaceMuted = lighten(primary, 0.97, '#F4F6FB');
  const surfaceElevated = lighten(primary, 0.86, '#FFFFFF');

  const border = mixHexColors('#000000', surface, 0.1, '#E2E8F0');

  const textStrong = mixHexColors('#111827', primary, 0.2, '#111827');
  const textBase = mixHexColors('#334155', primary, 0.28, '#334155');
  const textMuted = mixHexColors('#64748B', primary, 0.36, '#64748B');

  const accent = lighten(primary, 0.18, primary);
  const accentSoft = lighten(accent, 0.88, primarySoft);

  const footerBackground = darken(primary, 0.55, '#0B1120');
  const footerText = lighten(primaryContrast === '#FFFFFF' ? '#FFFFFF' : '#CBD5F5', 0.6, '#E2E8F0');

  return {
    mode: 'light',
    primary,
    primaryContrast,
    primaryStrong,
    primarySoft,
    primarySubtle,
    secondary,
    secondaryContrast,
    secondarySoft,
    background,
    surface,
    surfaceMuted,
    surfaceElevated,
    border,
    textStrong,
    textBase,
    textMuted,
    accent,
    accentSoft,
    footerBackground,
    footerText,
  };
};

const deriveDarkTheme = (primaryInput: string, secondaryInput: string): DerivedTheme => {
  const DARK_PRESET = {
    background: '#141216',
    surface: '#171719',
    surfaceMuted: '#1C1C1F',
    surfaceElevated: '#222228',
    border: '#2F2F33',
    content: '#1C1C1F',
    contentBorder: '#2A2A2E',
    footer: '#1B1B1E',
    footerBorder: '#2C2C30',
    mutedText: '#A1A6B1',
    bodyText: '#E8EAF2',
  } as const;

  const primaryBase = sanitizeColor(primaryInput, DEFAULT_PRIMARY_COLOR);
  const secondaryBase = sanitizeColor(secondaryInput, DEFAULT_SECONDARY_COLOR);

  const safePrimary = lighten(primaryBase, 0.12, primaryBase);
  const safeSecondary = secondaryBase;

  const accentReadable = ensureReadableOnDark(safeSecondary, DEFAULT_SECONDARY_COLOR, 0.55);
  const secondaryMuted = ensureReadableOnDark(
    mixHexColors(safeSecondary, DARK_PRESET.mutedText, 0.35, DARK_PRESET.mutedText),
    DARK_PRESET.mutedText,
    0.6,
  );
  const footerReadable = ensureReadableOnDark(
    mixHexColors(safeSecondary, DARK_PRESET.bodyText, 0.35, DARK_PRESET.bodyText),
    DARK_PRESET.bodyText,
    0.68,
  );

  const primary = lighten(primaryBase, 0.28, primaryBase);
  const primaryContrast = getContrastText(primary, '#0B1120', '#FFFFFF');
  const primaryStrong = mixHexColors(primaryBase, '#0C101F', 0.58, primaryBase);
  const primarySoft = mixHexColors(DARK_PRESET.surfaceMuted, primary, 0.4, lighten(primaryBase, 0.65, primaryBase));
  const primarySubtle = mixHexColors(DARK_PRESET.surface, primarySoft, 0.32, lighten(primaryBase, 0.75, primaryBase));

  const secondary = safeSecondary;
  const secondarySoft = mixHexColors(DARK_PRESET.surfaceMuted, secondary, 0.42, lighten(secondary, 0.55, secondary));
  const secondaryContrast = '#FFFFFF';

  const background = mixHexColors(DARK_PRESET.content, safePrimary, 0.72, DARK_PRESET.content);
  const surface = mixHexColors(DARK_PRESET.surface, safePrimary, 0.78, DARK_PRESET.surface);
  const surfaceMuted = mixHexColors(DARK_PRESET.surfaceMuted, safePrimary, 0.6, DARK_PRESET.surfaceMuted);
  const surfaceElevated = mixHexColors(DARK_PRESET.surfaceElevated, safePrimary, 0.55, DARK_PRESET.surfaceElevated);
  const border = mixHexColors(DARK_PRESET.border, safePrimary, 0.55, DARK_PRESET.border);

  const textStrong = ensureReadableOnDark(
    mixHexColors(DARK_PRESET.bodyText, secondaryBase, 0.12, DARK_PRESET.bodyText),
    DARK_PRESET.bodyText,
    0.82,
  );
  const textBase = ensureReadableOnDark(
    mixHexColors('#D7DBE8', secondaryBase, 0.18, '#D7DBE8'),
    '#D7DBE8',
    0.7,
  );
  const textMuted = secondaryMuted;

  const accent = accentReadable;
  const accentSoft = mixHexColors(accentReadable, '#242A3D', 0.5, lighten(accentReadable, 0.65, accentReadable));

  const footerBackground = mixHexColors(DARK_PRESET.footer, safePrimary, 0.7, DARK_PRESET.footer);
  const footerText = footerReadable;

  return {
    mode: 'dark',
    primary,
    primaryContrast,
    primaryStrong,
    primarySoft,
    primarySubtle,
    secondary,
    secondaryContrast,
    secondarySoft,
    background,
    surface,
    surfaceMuted,
    surfaceElevated,
    border,
    textStrong,
    textBase,
    textMuted,
    accent,
    accentSoft,
    footerBackground,
    footerText,
  };
};

const deriveTheme = (primaryInput: string, secondaryInput: string, mode: ThemeMode): DerivedTheme => {
  return mode === 'dark'
    ? deriveDarkTheme(primaryInput, secondaryInput)
    : deriveLightTheme(primaryInput, secondaryInput);
};

const applyVariables = (theme: DerivedTheme, logoUrl?: string | null) => {
  if (typeof document === 'undefined') {
    return;
  }

  const root = document.documentElement;
  root.style.setProperty('--brand-primary', theme.primary);
  root.style.setProperty('--brand-primary-contrast', theme.primaryContrast);
  root.style.setProperty('--brand-primary-strong', theme.primaryStrong);
  root.style.setProperty('--brand-primary-soft', theme.primarySoft);
  root.style.setProperty('--brand-primary-subtle', theme.primarySubtle);

  root.style.setProperty('--brand-secondary', theme.secondary);
  root.style.setProperty('--brand-secondary-contrast', theme.secondaryContrast);
  root.style.setProperty('--brand-secondary-soft', theme.secondarySoft);

  root.style.setProperty('--brand-background', theme.background);
  root.style.setProperty('--brand-surface', theme.surface);
  root.style.setProperty('--brand-surface-muted', theme.surfaceMuted);
  root.style.setProperty('--brand-surface-elevated', theme.surfaceElevated);
  root.style.setProperty('--brand-border', theme.border);

  root.style.setProperty('--brand-text-strong', theme.textStrong);
  root.style.setProperty('--brand-text-base', theme.textBase);
  root.style.setProperty('--brand-text-muted', theme.textMuted);

  root.style.setProperty('--brand-accent', theme.accent);
  root.style.setProperty('--brand-accent-soft', theme.accentSoft);
  root.style.setProperty('--brand-footer-background', theme.footerBackground);
  root.style.setProperty('--brand-footer-text', theme.footerText);

  root.style.setProperty('--color-slyrsa-primary', theme.primary);
  root.style.setProperty('--color-slyrsa-secondary', theme.textBase);
  root.style.setProperty('--color-slyrsa-accent-blue-light', theme.primarySoft);
  root.style.setProperty('--color-slyrsa-accent-blue-medium', theme.primary);
  root.style.setProperty('--color-slyrsa-accent-blue-dark', theme.primaryStrong);
  root.style.setProperty('--color-slyrsa-accent-gray-light', theme.textMuted);
  root.style.setProperty('--color-slyrsa-accent-gray-medium', theme.textBase);
  root.style.setProperty('--color-slyrsa-accent-gray-dark', theme.textStrong);

  if (logoUrl) {
    root.style.setProperty('--brand-logo-url', `url('${logoUrl}')`);
  } else {
    root.style.removeProperty('--brand-logo-url');
  }

  root.dataset.theme = theme.mode;
  root.style.setProperty('color-scheme', theme.mode === 'dark' ? 'dark' : 'light');

  document.body.style.backgroundColor = theme.background;
  document.body.style.color = theme.textBase;
  document.body.dataset.theme = theme.mode;
};

export const applySystemTheme = (settings: SystemSettingsResponse, mode: ThemeMode): void => {
  const theme = deriveTheme(settings.primaryColor, settings.secondaryColor, mode);
  applyVariables(theme, settings.logoUrl);
};

export const applySystemThemeFromDefaults = (mode: ThemeMode): void => {
  const theme = deriveTheme(DEFAULT_PRIMARY_COLOR, DEFAULT_SECONDARY_COLOR, mode);
  applyVariables(theme, null);
};

export const resetSystemTheme = (mode: ThemeMode): void => {
  applySystemThemeFromDefaults(mode);
};

export type { DerivedTheme };
export { deriveTheme, mixHexColors, relativeLuminance, lighten, darken };
